import React, { useState } from 'react';
import { X, FileText, ArrowRight, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { formatCurrency, formatDate } from '../../lib/utils';
import { quoteService } from '../../lib/database/quotes';

interface ConvertToInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  quote: any;
}

export const ConvertToInvoiceModal: React.FC<ConvertToInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  quote,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    // Default to 30 days from now
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  });

  const handleConvert = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!dueDate) {
        setError('Please select a due date for the invoice');
        return;
      }

      const { data: invoice, error: convertError } = await quoteService.convertToInvoice(
        quote.id,
        dueDate
      );

      if (convertError) {
        setError('Failed to convert quote to invoice: ' + convertError.message);
        return;
      }

      if (invoice) {
        setSuccess('Quote successfully converted to invoice!');
        
        // Close modal and refresh after delay
        setTimeout(() => {
          onSuccess();
          onClose();
          setError('');
          setSuccess('');
        }, 2000);
      }
    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !quote) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-secondary-50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Convert to Invoice</h2>
              <p className="text-gray-600">Transform this quote into an invoice</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && <Alert type="error" message={error} />}
          {success && <Alert type="success" message={success} />}

          {/* Quote Summary */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Quote Number</p>
                <p className="font-semibold text-gray-900">{quote.quote_number}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Client</p>
                <p className="font-semibold text-gray-900">{quote.client?.name}</p>
                <p className="text-sm text-gray-500">{quote.client?.company}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="font-bold text-xl text-primary-600">{formatCurrency(Number(quote.total))}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Valid Until</p>
                <p className="font-semibold text-gray-900">{formatDate(quote.valid_until)}</p>
              </div>
            </div>
          </div>

          {/* Conversion Arrow */}
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-primary-100 px-4 py-2 rounded-lg">
                <FileText className="w-5 h-5 text-primary-600" />
                <span className="font-medium text-primary-700">Quote</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400" />
              <div className="flex items-center space-x-2 bg-success-100 px-4 py-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-success-600" />
                <span className="font-medium text-success-700">Invoice</span>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Invoice Settings</h3>
            
            <FormField label="Due Date" icon={Calendar} required>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </FormField>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h4 className="text-blue-800 font-semibold mb-2">What happens when you convert?</h4>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>• A new invoice will be created with the same items and amounts</li>
                <li>• The quote status will be updated to "Approved"</li>
                <li>• All quote items will be copied to the new invoice</li>
                <li>• The client will remain the same</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              icon={FileText}
              loading={loading}
              disabled={loading}
              onClick={handleConvert}
              className="bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700"
            >
              Convert to Invoice
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};